import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api, toast } from "../App";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, GanttChartSquare, ArrowLeft, Filter, ChevronRight, ChevronDown } from "lucide-react";
import Gantt from "frappe-gantt";
import "../styles/frappe-gantt.css";
import { fh } from "@/utils/hours";

const VIEW_MODES = ["Day", "Week", "Month"];

export default function PlanningBoard({ user }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const ganttContainerRef = useRef(null);
  const ganttInstanceRef = useRef(null);
  
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [viewMode, setViewMode] = useState("Week");
  const [search, setSearch] = useState("");
  const [filterMine, setFilterMine] = useState(false);
  const [filterWorkstreamOnly, setFilterWorkstreamOnly] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState({});

  const fetchBoard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/planning/board/${projectId}`);
      setBoard(res.data);
      // Auto-expand all by default
      const exp = {};
      const walk = (nodes) => {
        for (const n of nodes || []) {
          exp[n.id] = true;
          if (n.children) walk(n.children);
        }
      };
      walk(res.data?.tree || []);
      setExpandedNodes(exp);
    } catch {
      toast.error("Failed to load planning board");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  const filteredTasks = useCallback(() => {
    let tasks = board?.tasks || [];
    if (filterMine) tasks = tasks.filter(t => t.assigned_to === user?.id);
    if (filterWorkstreamOnly) tasks = tasks.filter(t => ["modeling", "detailing", "erection", "model_checking", "detail_checking", "erection_checking"].includes(t.activity_type));
    if (search.trim()) {
      const q = search.toLowerCase();
      tasks = tasks.filter(t => (t.description || "").toLowerCase().includes(q) || (t.assigned_user_name || "").toLowerCase().includes(q));
    }
    return tasks;
  }, [board, filterMine, filterWorkstreamOnly, search, user]);

  // Build/refresh Gantt
  useEffect(() => {
    if (loading || !board || !ganttContainerRef.current) return;
    const tasks = filteredTasks();
    if (tasks.length === 0) {
      ganttContainerRef.current.innerHTML = '<div class="text-center py-10 text-gray-400 text-sm">No tasks match the current filters.</div>';
      ganttInstanceRef.current = null;
      return;
    }
    const ganttTasks = tasks.map(t => ({
      id: t.id,
      name: `${t.description || "(no description)"}`,
      start: t.start,
      end: t.end,
      progress: t.progress,
      dependencies: (t.dependencies || []).join(","),
      custom_class: colorClass(t),
    }));
    ganttContainerRef.current.innerHTML = "";
    try {
      ganttInstanceRef.current = new Gantt(ganttContainerRef.current, ganttTasks, {
        view_mode: viewMode,
        bar_height: 24,
        padding: 18,
        date_format: "YYYY-MM-DD",
        on_click: (task) => {
          const fullTask = (board?.tasks || []).find(t => t.id === task.id);
          if (fullTask) setSelectedTask(fullTask);
        },
        on_date_change: async (task, start, end) => {
          const newStart = start.toISOString().slice(0, 10);
          const newEnd = end.toISOString().slice(0, 10);
          try {
            await api.put(`/planning/tasks/${task.id}/dates`, { planned_start_date: newStart, due_date: newEnd });
            toast.success("Task dates updated");
            fetchBoard();
          } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to update dates");
            fetchBoard();
          }
        },
        on_progress_change: () => {},  // disabled — progress comes from work logs
      });
    } catch (e) {
      console.error("Gantt render error", e);
    }
  }, [board, loading, viewMode, filterMine, filterWorkstreamOnly, search, filteredTasks, fetchBoard]);

  const colorClass = (t) => {
    if (t.workflow_state === "done") return "bar-done";
    const today = new Date().toISOString().slice(0, 10);
    if (t.end < today && t.workflow_state !== "done") return "bar-overdue";
    if (t.role === "checker") return "bar-checker";
    return "bar-producer";
  };

  const renderTree = (nodes, depth = 0) => (
    <ul className="text-xs">
      {(nodes || []).map(n => {
        const isExpanded = expandedNodes[n.id] !== false;
        const hasChildren = (n.children || []).length > 0;
        return (
          <li key={n.id} className="mb-1">
            <div
              className="flex items-center gap-1 cursor-pointer hover:bg-md3-surface-container-high rounded px-1 py-0.5"
              style={{ paddingLeft: `${depth * 12}px` }}
              onClick={() => hasChildren ? setExpandedNodes(s => ({ ...s, [n.id]: !isExpanded })) : (n.type === "task" && setSelectedTask((board?.tasks || []).find(t => t.id === n.id)))}
            >
              {hasChildren ? (isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />) : <span className="w-3" />}
              <span className={`truncate ${n.type === "phase" ? "font-bold" : n.type === "scope" ? "font-semibold" : ""}`}>
                {n.name}
              </span>
              {n.role && (
                <Badge variant="outline" className="text-[9px] ml-1">{n.role}</Badge>
              )}
            </div>
            {hasChildren && isExpanded && renderTree(n.children, depth + 1)}
          </li>
        );
      })}
    </ul>
  );

  return (
    <Layout user={user}>
      <div className="p-6 space-y-4">
        <div className="flex justify-between items-end gap-3">
          <div>
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="-ml-2 mb-1 text-md3-on-surface-variant hover:text-md3-on-surface">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <h1 className="text-md3-display-small font-semibold flex items-center gap-3">
              <GanttChartSquare className="h-7 w-7 text-md3-primary" />
              Planning Board
            </h1>
            {board?.project && (
              <p className="text-md3-on-surface-variant text-sm font-mono">
                {board.project.project_number} — {board.project.name}
              </p>
            )}
          </div>
          <div className="flex gap-2 items-end flex-wrap">
            <div>
              <label className="text-xs text-md3-on-surface-variant block mb-1">View</label>
              <div className="flex gap-1 bg-md3-surface-container rounded p-1">
                {VIEW_MODES.map(m => (
                  <button
                    key={m}
                    onClick={() => setViewMode(m)}
                    className={`px-3 py-1 text-xs rounded ${viewMode === m ? "bg-md3-primary text-md3-on-primary" : "hover:bg-md3-surface-container-high"}`}
                    data-testid={`gantt-view-${m.toLowerCase()}`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-md3-on-surface-variant block mb-1">Search</label>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Task / assignee" className="w-56 bg-white" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-md3-on-surface-variant flex items-center gap-1">
                <input type="checkbox" checked={filterMine} onChange={(e) => setFilterMine(e.target.checked)} data-testid="gantt-filter-mine" />
                Mine only
              </label>
              <label className="text-xs text-md3-on-surface-variant flex items-center gap-1">
                <input type="checkbox" checked={filterWorkstreamOnly} onChange={(e) => setFilterWorkstreamOnly(e.target.checked)} data-testid="gantt-filter-workstream" />
                Workstreams only
              </label>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-md3-primary" /></div>
        ) : (
          <div className="grid grid-cols-12 gap-3 min-h-[70vh]">
            {/* LEFT: tree */}
            <Card className="col-span-3 shadow-md3-1 border-0 bg-md3-surface-container overflow-y-auto max-h-[78vh]">
              <CardContent className="p-3">
                <div className="text-xs text-md3-on-surface-variant font-semibold mb-2">Hierarchy</div>
                {renderTree(board?.tree || [])}
              </CardContent>
            </Card>

            {/* CENTER: gantt */}
            <Card className="col-span-6 shadow-md3-1 border-0 bg-white overflow-x-auto overflow-y-auto max-h-[78vh]">
              <CardContent className="p-2">
                <div ref={ganttContainerRef} className="gantt-container" data-testid="gantt-container" />
              </CardContent>
            </Card>

            {/* RIGHT: details panel */}
            <Card className="col-span-3 shadow-md3-1 border-0 bg-md3-surface-container max-h-[78vh] overflow-y-auto">
              <CardContent className="p-4 text-sm">
                {!selectedTask ? (
                  <div className="text-md3-on-surface-variant text-center py-10 text-xs">Click a bar to see details</div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <div className="text-xs text-md3-on-surface-variant">Task</div>
                      <div className="font-semibold">{selectedTask.description}</div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Badge variant="outline" className="capitalize text-[10px]">{(selectedTask.activity_type || "").replace(/_/g, " ")}</Badge>
                      {selectedTask.role && <Badge variant="outline" className="text-[10px]">{selectedTask.role}</Badge>}
                      {selectedTask.workflow_state && <Badge className="text-[10px] capitalize">{selectedTask.workflow_state.replace(/_/g, " ")}</Badge>}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <div className="text-md3-on-surface-variant">Start</div>
                        <div>{selectedTask.start}</div>
                      </div>
                      <div>
                        <div className="text-md3-on-surface-variant">Due</div>
                        <div>{selectedTask.end}</div>
                      </div>
                      <div>
                        <div className="text-md3-on-surface-variant">Allocated</div>
                        <div className="font-medium">{fh(selectedTask.allocated_hours)}h</div>
                      </div>
                      <div>
                        <div className="text-md3-on-surface-variant">Actual</div>
                        <div className="font-medium">{fh(selectedTask.actual_hours)}h</div>
                      </div>
                      <div>
                        <div className="text-md3-on-surface-variant">Progress</div>
                        <div className="font-medium">{selectedTask.progress}%</div>
                      </div>
                      <div>
                        <div className="text-md3-on-surface-variant">Assignee</div>
                        <div className="truncate">{selectedTask.assigned_user_name || "—"}</div>
                      </div>
                    </div>
                    {(selectedTask.current_cycle || 1) > 1 && (
                      <Badge className="bg-orange-100 text-orange-700">Rework Cycle {selectedTask.current_cycle}</Badge>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <style>{`
        .gantt .bar-producer .bar { fill: #5b8def; }
        .gantt .bar-checker .bar { fill: #14b8a6; }
        .gantt .bar-done .bar { fill: #22c55e; }
        .gantt .bar-overdue .bar { fill: #ef4444; }
        .gantt .bar-label { fill: #fff; font-size: 11px; }
        .gantt-container { min-height: 400px; }
      `}</style>
    </Layout>
  );
}
